'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useTransition } from '@/contexts/TransitionContext';

const PIXEL_SIZE = 8;
const ANT_ANIM_DURATION = 600;

const ANT_RECTS: Array<{ x: number; y: number; w: number; h: number; eye: boolean }> = [
  { x: 4, y: 0, w: 1, h: 1, eye: false },
  { x: 11, y: 0, w: 1, h: 1, eye: false },
  { x: 5, y: 1, w: 1, h: 2, eye: false },
  { x: 10, y: 1, w: 1, h: 2, eye: false },
  { x: 6, y: 3, w: 1, h: 1, eye: false },
  { x: 9, y: 3, w: 1, h: 1, eye: false },
  { x: 5, y: 4, w: 6, h: 1, eye: false },
  { x: 4, y: 5, w: 8, h: 1, eye: false },
  { x: 3, y: 6, w: 10, h: 1, eye: false },
  { x: 3, y: 7, w: 2, h: 1, eye: false },
  { x: 7, y: 7, w: 2, h: 1, eye: false },
  { x: 11, y: 7, w: 2, h: 1, eye: false },
  { x: 2, y: 8, w: 3, h: 1, eye: false },
  { x: 7, y: 8, w: 2, h: 1, eye: false },
  { x: 11, y: 8, w: 3, h: 1, eye: false },
  { x: 5, y: 7, w: 2, h: 2, eye: true },
  { x: 9, y: 7, w: 2, h: 2, eye: true },
  { x: 2, y: 9, w: 12, h: 1, eye: false },
  { x: 3, y: 10, w: 10, h: 1, eye: false },
  { x: 4, y: 11, w: 8, h: 1, eye: false },
  { x: 5, y: 12, w: 6, h: 1, eye: false },
  { x: 4, y: 13, w: 2, h: 1, eye: false },
  { x: 10, y: 13, w: 2, h: 1, eye: false },
  { x: 3, y: 14, w: 2, h: 1, eye: false },
  { x: 11, y: 14, w: 2, h: 1, eye: false },
  { x: 2, y: 15, w: 1, h: 1, eye: false },
  { x: 13, y: 15, w: 1, h: 1, eye: false },
];

function stampAntOnCanvas(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  bodyColor: string,
  eyeColor: string,
  alpha = 1,
) {
  const scale = size / 16;
  const ox = cx - size / 2;
  const oy = cy - size / 2;

  for (const r of ANT_RECTS) {
    ctx.fillStyle = r.eye ? eyeColor : bodyColor;
    ctx.globalAlpha = (r.eye ? 0.9 : 1) * alpha;
    ctx.fillRect(ox + r.x * scale, oy + r.y * scale, r.w * scale, r.h * scale);
  }
  ctx.globalAlpha = 1;
}

/** Attempt to mimic cubic-bezier(0.16, 1, 0.3, 1) -- strong ease-out */
function easeOutExpo(t: number): number {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function shuffleArray(arr: number[]) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

export default function SwarmTransition() {
  const { phase } = useTransition();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef(0);
  const fillOrderRef = useRef<number[]>([]);
  const clearOrderRef = useRef<number[]>([]);
  const filledRef = useRef(0);
  const clearedRef = useRef(0);
  const gridRef = useRef({ cols: 0, rows: 0, total: 0 });
  const startTimeRef = useRef(0);

  const initGrid = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const w = parent.clientWidth;
    const h = parent.clientHeight;
    canvas.width = w;
    canvas.height = h;

    const cols = Math.ceil(w / PIXEL_SIZE);
    const rows = Math.ceil(h / PIXEL_SIZE);
    const total = cols * rows;
    gridRef.current = { cols, rows, total };

    const indices = Array.from({ length: total }, (_, i) => i);
    fillOrderRef.current = shuffleArray([...indices]);
    clearOrderRef.current = shuffleArray([...indices]);
    filledRef.current = 0;
    clearedRef.current = 0;
  }, []);

  useEffect(() => {
    if (phase === 'disintegrate') {
      initGrid();
      startTimeRef.current = performance.now();

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const { cols, total } = gridRef.current;
      const surfaceColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-surface')
        .trim();

      const animate = (now: number) => {
        const elapsed = now - startTimeRef.current;
        const targetCount = Math.min(
          total,
          Math.floor((elapsed / 800) * total),
        );

        ctx.fillStyle = surfaceColor;
        while (filledRef.current < targetCount) {
          const idx = fillOrderRef.current[filledRef.current];
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          ctx.fillRect(col * PIXEL_SIZE, row * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
          filledRef.current++;
        }

        if (filledRef.current < total) {
          animRef.current = requestAnimationFrame(animate);
        }
      };

      animRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animRef.current);
    }

    if (phase === 'ant-reveal') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const styles = getComputedStyle(document.documentElement);
      const surfaceColor = styles.getPropertyValue('--color-surface').trim();
      const bodyColor = styles.getPropertyValue('--color-primary').trim();
      const eyeColor = styles.getPropertyValue('--color-amber').trim();
      const antSize = window.matchMedia('(min-width: 768px)').matches ? 192 : 128;
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      startTimeRef.current = performance.now();

      const animate = (now: number) => {
        const elapsed = now - startTimeRef.current;
        const rawT = Math.min(1, elapsed / ANT_ANIM_DURATION);
        const t = easeOutExpo(rawT);

        const currentScale = 0.4 + 0.6 * t;
        const currentAlpha = t;
        const currentSize = antSize * currentScale;

        const clearHalf = (antSize / 2) + 2;
        ctx.fillStyle = surfaceColor;
        ctx.fillRect(cx - clearHalf, cy - clearHalf, clearHalf * 2, clearHalf * 2);

        stampAntOnCanvas(ctx, cx, cy, currentSize, bodyColor, eyeColor, currentAlpha);

        if (rawT < 1) {
          animRef.current = requestAnimationFrame(animate);
        }
      };

      animRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animRef.current);
    }

    if (phase === 'done') {
      startTimeRef.current = performance.now();

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const { cols, total } = gridRef.current;

      const animate = (now: number) => {
        const elapsed = now - startTimeRef.current;
        const targetCount = Math.min(
          total,
          Math.floor((elapsed / 1100) * total),
        );

        while (clearedRef.current < targetCount) {
          const idx = clearOrderRef.current[clearedRef.current];
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          ctx.clearRect(col * PIXEL_SIZE, row * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
          clearedRef.current++;
        }

        if (clearedRef.current < total) {
          animRef.current = requestAnimationFrame(animate);
        }
      };

      animRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animRef.current);
    }

    if (phase === 'idle') {
      cancelAnimationFrame(animRef.current);
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      filledRef.current = 0;
      clearedRef.current = 0;
    }
  }, [phase, initGrid]);

  if (phase === 'idle') return null;

  return (
    <div className="absolute inset-0 z-40 pointer-events-none overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}
